import React, { useMemo, useState } from "react";
import "./OnboardingMaterials.css";
import { getAPIURL } from "../../api/config";

const presentationFileName = "Welcome презентация — 1237.pptx";
const buildPresentationUrl = () => {
  const apiBaseUrl = getAPIURL().replace(/\/+$/, "");
  const encodedFileName = encodeURIComponent(presentationFileName);
  return `${apiBaseUrl}/images/posts/${encodedFileName}`;
};

const presentationUrl = buildPresentationUrl();

const presentation = {
  title: "Welcome презентация",
  description:
    "Презентация знакомит с историей компании, структурой, ключевыми проектами и корпоративной культурой.",
  pages: 22,
  type: "PPTX",
  size: "62 MB",
  openUrl: presentationUrl,
  downloadUrl: presentationUrl,
};

const lessons = [
  {
    id: 1,
    title: "Создание отчета",
    description: "Как создавать отчеты о проделанной работе?",
    duration: "0:33",
    youtubeEmbedUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
  },
  {
    id: 2,
    title: "Создание отчета с несколькими проектами",
    description: "Как создать отчеты с несколькими проектами?",
    duration: "0:57",
    youtubeEmbedUrl: "https://www.youtube.com/embed/9bZkp7q19f0",
  },
  {
    id: 3,
    title: "Редактирование отчета",
    description: "Как редактировать отчеты?",
    duration: "0:34",
    youtubeEmbedUrl: "https://www.youtube.com/embed/kXYiU_JCYtU",
  },
  {
    id: 4,
    title: "Как пользоваться календарем?",
    description: "Как пользоваться календарем?",
    duration: "0:23",
    youtubeEmbedUrl: "https://www.youtube.com/embed/3JZ_D3ELwOQ",
  },
];

function OnboardingMaterials() {
  const [activeLessonId, setActiveLessonId] = useState(lessons[0].id);
  const activeLesson = useMemo(
    () => lessons.find((lesson) => lesson.id === activeLessonId) || lessons[0],
    [activeLessonId]
  );

  const handleLessonCardKeyDown = (event, lessonId) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setActiveLessonId(lessonId);
    }
  };

  return (
    <section className="onboarding-materials">
      <header className="onboarding-materials__header">
        <h1 className="onboarding-materials__title">Материалы для адаптации</h1>
        <p className="onboarding-materials__subtitle">
          Здесь собраны материалы, которые помогут быстро познакомиться с компанией,
          процессами и порталом.
        </p>
      </header>

      <div className="onboarding-materials__roadmap">
        <div className="onboarding-materials__roadmap-main">
          <h2 className="onboarding-materials__roadmap-title">С чего начать</h2>
          <ol className="onboarding-materials__steps">
            <li>
              <span className="onboarding-materials__step-number">1</span>
              <div>
                <p className="onboarding-materials__step-name">
                  Посмотрите welcome-презентацию
                </p>
                <p className="onboarding-materials__step-desc">
                  Узнайте о компании, структуре и основных принципах работы
                </p>
              </div>
            </li>
            <li>
              <span className="onboarding-materials__step-number">2</span>
              <div>
                <p className="onboarding-materials__step-name">
                  Посмотрите вводные видео
                </p>
                <p className="onboarding-materials__step-desc">
                  Короткие ролики помогут освоить портал и процессы
                </p>
              </div>
            </li>
            <li>
              <span className="onboarding-materials__step-number">3</span>
              <div>
                <p className="onboarding-materials__step-name">
                  Перейдите к работе в системе
                </p>
                <p className="onboarding-materials__step-desc">
                  Изучите ключевые разделы портала
                </p>
              </div>
            </li>
          </ol>
        </div>

        <div className="onboarding-materials__roadmap-meta">
          <p>Всего материалов</p>
          <h3>1 презентация + 4 видео</h3>
          <span>Примерно 15-20 минут</span>
        </div>
      </div>

      <h2 className="onboarding-materials__section-title">Welcome-презентация</h2>
      <article className="onboarding-materials__presentation-card">
        <div className="onboarding-materials__presentation-icon">📄</div>
        <div className="onboarding-materials__presentation-content">
          <h3>{presentation.title}</h3>
          <p>{presentation.description}</p>
          <span>
            {presentation.pages} слайдa • {presentation.type}, {presentation.size}
          </span>
        </div>
        <div className="onboarding-materials__presentation-actions">
          <a href={presentation.openUrl} target="_blank" rel="noreferrer">
            Открыть
          </a>
          <a href={presentation.downloadUrl} download>
            Скачать
          </a>
        </div>
      </article>

      <h2 className="onboarding-materials__section-title">Видеоматериалы</h2>
      <div className="onboarding-materials__video-layout">
        <div className="onboarding-materials__video-player">
          <iframe
            key={activeLesson.id}
            src={activeLesson.youtubeEmbedUrl}
            title={activeLesson.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          <div className="onboarding-materials__video-caption">
            Сейчас выбран: {activeLesson.title}
          </div>
        </div>

        <div className="onboarding-materials__video-list">
          {lessons.map((lesson) => (
            <article
              key={lesson.id}
              role="button"
              tabIndex={0}
              aria-label={`Выбрать видео: ${lesson.title}`}
              aria-current={lesson.id === activeLesson.id ? "true" : undefined}
              className={`onboarding-materials__video-item${
                lesson.id === activeLesson.id
                  ? " onboarding-materials__video-item--active"
                  : ""
              }`}
              onClick={() => setActiveLessonId(lesson.id)}
              onKeyDown={(event) => handleLessonCardKeyDown(event, lesson.id)}
            >
              <div className="onboarding-materials__video-thumb">
                <span>{lesson.duration}</span>
              </div>
              <div className="onboarding-materials__video-info">
                <div className="onboarding-materials__video-row">
                  <strong>{lesson.id}</strong>
                  <h3>{lesson.title}</h3>
                </div>
                <p>{lesson.description}</p>
                <span className="onboarding-materials__video-watch">Смотреть</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default OnboardingMaterials;
